// Feature: docs/features/payer-response-intelligence-v2.md
// Feature: docs/features/payer-response-chat.md
// Feature: docs/features/payer-response-auto-load.md
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Select,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
  Wrap,
  WrapItem,
  Checkbox,
  Divider,
  SimpleGrid,
  Spinner,
  Flex
} from '@chakra-ui/react';
import React, { useState, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import ModernChat from './ModernChat';

const PayerResponseIntelligence = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const [claimIds, setClaimIds] = useState([]);
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [includeCodingVariance, setIncludeCodingVariance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Case Context & Document Viewer State
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [docUrl, setDocUrl] = useState(null);
  const [isDocLoading, setIsDocLoading] = useState(false);

  // Demo Data for PDF Export
  const ENTITIES = [
    { id: 'group-x', name: 'Group X Medical Services', address: '123 Generic Blvd, Suite 100, City, ST 12345' },
    { id: 'group-y', name: 'Group Y Surgical Group', address: '456 Example Way, City, ST 67890' }
  ];

  const SIGNATURES = [
    { id: 'rep-a', text: `Representative A, Senior AR Specialist\nExt. 100 | Direct: (555) 123-4567` },
    { id: 'rep-b', text: `Representative B, Appeals Coordinator\nExt. 200 | Direct: (555) 987-6543` }
  ];

  const [selectedEntityId, setSelectedEntityId] = useState(ENTITIES[0].id);
  const [selectedSignatureId, setSelectedSignatureId] = useState(SIGNATURES[0].id);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Fetch available claim IDs, latest analysis, and case context on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!caseId || !accessToken) return;
      try {
        // 1. Fetch claim IDs
        const claimsPromise = callApi(`/cases/${caseId}/claims`, accessToken);
        
        // 2. Fetch latest analysis
        const latestPromise = callApi(`/api/v2/cases/${caseId}/payer-response/latest`, accessToken);
        
        // 3. Fetch case details (for files)
        const detailsPromise = callApi(`/get_case_details/${caseId}`, accessToken);
        
        // 4. Fetch workflow status (for payer info)
        const workflowPromise = callApi(`/workflow_status/${caseId}`, accessToken);

        const [claimsResponse, latestResponse, detailsResponse, workflowResponse] = await Promise.all([
          claimsPromise, latestPromise, detailsPromise, workflowPromise
        ]);

        // Process Claims
        const claimsData = await claimsResponse.json();
        if (claimsResponse.ok && claimsData.status === 'success') {
          setClaimIds(claimsData.claim_ids || []);
        }

        // Process Latest Analysis
        const latestData = await latestResponse.json();
        if (latestResponse.ok && latestData.success && latestData.data) {
          let parsedData = { ...latestData.data };
          if (typeof parsedData.draft_content === 'string') {
            try {
              parsedData.draft_content = JSON.parse(parsedData.draft_content);
            } catch (e) {
              console.error("Failed to parse draft_content string", e);
            }
          }
          setAnalysisResult(parsedData);
          if (parsedData.claim_type) {
            setSelectedClaimId(parsedData.claim_type);
          }
        }

        // Process Case Details (Files)
        const detailsData = await detailsResponse.json();
        if (detailsResponse.ok) {
          setCaseFiles(detailsData.files || []);
        }

        // Process Workflow Status
        const workflowData = await workflowResponse.json();
        if (workflowResponse.ok) {
          setWorkflowStatus(workflowData);
        }

      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };
    fetchInitialData();
  }, [caseId, accessToken]);

  const handleFileSelect = async (fileId) => {
    setSelectedFileId(fileId);
    
    if (!fileId) {
      if (docUrl) window.URL.revokeObjectURL(docUrl);
      setDocUrl(null);
      return;
    }

    setIsDocLoading(true);
    if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
    }
    setDocUrl(null);

    try {
        const response = await callApi('/download_case_file', accessToken, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                case_id: caseId,
                file_id: fileId,
                disposition: 'inline'
            }),
        });

        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setDocUrl(url);
        } else {
            throw new Error('Failed to load file');
        }
    } catch (error) {
        console.error('Error loading file:', error);
        toast({
            title: 'Failed to load file',
            status: 'error',
            duration: 3000,
        });
    } finally {
        setIsDocLoading(false);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
      }
    };
  }, [docUrl]);

  const handleAnalyze = async () => {
    if (!selectedClaimId) {
      toast({
        title: 'Please select a claim ID.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setAnalysisResult(null);

    try {
      const response = await callApi(`/api/v2/cases/${caseId}/claims/${selectedClaimId}/analyze-eob`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          include_coding_variance: includeCodingVariance
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAnalysisResult(data);
        toast({
          title: 'Analysis Complete',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(data.error || 'Failed to analyze EOB');
      }
    } catch (err) {
      toast({
        title: 'Analysis Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!analysisResult || !analysisResult.draft_content) return;
    
    setIsExportingPdf(true);
    try {
      const entity = ENTITIES.find(e => e.id === selectedEntityId);
      const signature = SIGNATURES.find(s => s.id === selectedSignatureId);

      // For the PDF, we'll just send the appeal letter part if it exists, 
      // otherwise the memo, or a combined version if both exist.
      let contentToExport = {};
      if (analysisResult.draft_content.appeal_letter) {
         contentToExport.clinical_narrative = analysisResult.draft_content.appeal_letter;
      } else if (analysisResult.draft_content.corrected_claim_memo) {
         contentToExport.clinical_narrative = analysisResult.draft_content.corrected_claim_memo;
      }

      const response = await callApi(`/api/v2/cases/${caseId}/claims/${selectedClaimId}/generate-appeal-pdf`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entity_name: entity.name,
          entity_address: entity.address,
          signature_block: signature.text,
          draft_content: contentToExport
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Appeal_Letter_${selectedClaimId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'PDF Exported',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Export Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const cleanMarkdownText = (text) => {
    if (!text) return '';
    return text.replace(/\n\s+/g, '\n\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  const MarkdownComponents = {
    p: (props) => <Text whiteSpace="pre-wrap" mb={4} {...props} />,
    h1: (props) => <Heading size="xl" mt={6} mb={4} {...props} />,
    h2: (props) => <Heading size="lg" mt={6} mb={4} {...props} />,
    h3: (props) => <Heading size="md" mt={6} mb={4} {...props} />,
    h4: (props) => <Heading size="sm" mt={4} mb={2} {...props} />,
    ul: (props) => <Box as="ul" pl={6} mb={4} {...props} />,
    ol: (props) => <Box as="ol" pl={6} mb={4} {...props} />,
    li: (props) => <Box as="li" mb={1} {...props} />,
    a: (props) => <Link color="blue.500" isExternal {...props} />,
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>Payer Response Intelligence Engine</Heading>
          <Text color="gray.600">
            V2 Pipeline: EOB Extraction & Comprehensive Draft Generation
          </Text>
        </Box>

        {/* Case Context Card */}
        {caseId && (
          <Card bg="white" borderColor="gray.200">
            <CardHeader p={3} bg="gray.50" borderBottomWidth="1px">
              <Heading size="sm">Case Context</Heading>
            </CardHeader>
            <CardBody p={4}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">PAYER</Text>
                  <Text fontSize="lg">
                    {workflowStatus?.step2_carrier_assignment?.carrier_name || 'Not specified'} 
                    {workflowStatus?.step2_carrier_assignment?.payer_type ? ` (${workflowStatus.step2_carrier_assignment.payer_type})` : ''}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">STATE</Text>
                  <Text fontSize="lg">{workflowStatus?.step2_carrier_assignment?.state || 'Not specified'}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">CASE ID</Text>
                  <Text fontSize="lg">{caseId}</Text>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody>
            <HStack spacing={4} align="flex-end">
              <Box flex="1">
                <Text fontSize="sm" fontWeight="medium" mb={2}>Select Claim</Text>
                <Select
                  placeholder="Select a claim to analyze"
                  value={selectedClaimId}
                  onChange={(e) => setSelectedClaimId(e.target.value)}
                >
                  {claimIds.map((id) => (
                    <option key={id} value={id}>{id}</option>
                  ))}
                </Select>
              </Box>
              <Box pb={2}>
                <Checkbox 
                  isChecked={includeCodingVariance}
                  onChange={(e) => setIncludeCodingVariance(e.target.checked)}
                  colorScheme="blue"
                >
                  Include Coding Variance Context
                </Checkbox>
              </Box>
              <Button
                colorScheme="blue"
                onClick={handleAnalyze}
                isLoading={isLoading}
                loadingText="Analyzing EOB..."
                isDisabled={!selectedClaimId}
              >
                {analysisResult ? "Re-run Analysis" : "Run EOB Analysis"}
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {analysisResult && (
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab fontWeight="medium">Draft & Evidence</Tab>
              <Tab fontWeight="medium">Source Documents</Tab>
              <Tab fontWeight="medium">Chat Assistant</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={6}>
                <VStack spacing={6} align="stretch">
                  
                  {/* Identified Issues Section */}
                  {analysisResult.draft_content && analysisResult.draft_content.issues_addressed && (
                    <Card borderLeft="4px solid" borderLeftColor="purple.500" shadow="md">
                      <CardHeader bg="gray.50" pb={2}>
                        <Heading size="md">Identified Issues</Heading>
                      </CardHeader>
                      <CardBody>
                        <Wrap spacing={3}>
                          {analysisResult.draft_content.issues_addressed.map((issue, idx) => (
                            <WrapItem key={idx}>
                              <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="full">
                                {issue}
                              </Badge>
                            </WrapItem>
                          ))}
                          {analysisResult.draft_content.issues_addressed.length === 0 && (
                            <Text color="gray.500">No specific issues identified.</Text>
                          )}
                        </Wrap>
                      </CardBody>
                    </Card>
                  )}

                  {/* Draft Letter Section */}
                  {analysisResult.draft_content && !analysisResult.draft_content.error ? (
                    <Card borderLeft="4px solid" borderLeftColor="green.500" shadow="md">
                      <CardHeader bg="gray.50" pb={2}>
                        <Heading size="md">Response Strategy & Drafts</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack align="stretch" spacing={6}>
                          
                          {/* Strategy Overview */}
                          {analysisResult.draft_content.strategy_overview && (
                            <Alert status="info" borderRadius="md">
                              <AlertIcon />
                              <Box>
                                <Text fontWeight="bold">Recommended Strategy</Text>
                                <Text>{analysisResult.draft_content.strategy_overview}</Text>
                              </Box>
                            </Alert>
                          )}

                          {/* Letter Customization Controls (Demo) */}
                          <Box bg="gray.50" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                            <Heading size="sm" mb={3}>Letter Customization</Heading>
                            <HStack spacing={4}>
                              <Box flex="1">
                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>PRACTICE ENTITY</Text>
                                <Select 
                                  size="sm" 
                                  bg="white"
                                  value={selectedEntityId} 
                                  onChange={(e) => setSelectedEntityId(e.target.value)}
                                >
                                  {ENTITIES.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </Select>
                              </Box>
                              <Box flex="1">
                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>SIGNATURE BLOCK</Text>
                                <Select 
                                  size="sm" 
                                  bg="white"
                                  value={selectedSignatureId} 
                                  onChange={(e) => setSelectedSignatureId(e.target.value)}
                                >
                                  {SIGNATURES.map(s => <option key={s.id} value={s.id}>{s.text.split('\n')[0]}</option>)}
                                </Select>
                              </Box>
                              <Box pt={5}>
                                <Button 
                                  colorScheme="red" 
                                  size="sm" 
                                  onClick={handleExportPdf}
                                  isLoading={isExportingPdf}
                                  loadingText="Generating..."
                                >
                                  Export to PDF
                                </Button>
                              </Box>
                            </HStack>
                          </Box>

                          {/* Corrected Claim Memo */}
                          {analysisResult.draft_content.corrected_claim_memo && (
                            <Box bg="white" p={6} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                              <Heading size="sm" color="blue.600" mb={4} textTransform="uppercase">Corrected Claim Memo</Heading>
                              <ReactMarkdown components={MarkdownComponents}>
                                {cleanMarkdownText(analysisResult.draft_content.corrected_claim_memo)}
                              </ReactMarkdown>
                            </Box>
                          )}

                          {/* Formal Appeal Letter */}
                          {analysisResult.draft_content.appeal_letter && (
                            <Box bg="white" p={6} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                              <Heading size="sm" color="blue.600" mb={4} textTransform="uppercase">Formal Appeal Letter</Heading>
                              <ReactMarkdown components={MarkdownComponents}>
                                {cleanMarkdownText(analysisResult.draft_content.appeal_letter)}
                              </ReactMarkdown>
                            </Box>
                          )}

                        </VStack>
                      </CardBody>
                    </Card>
                  ) : (
                    <Alert status="warning">
                      <AlertIcon />
                      Draft content is not available or an error occurred during generation.
                    </Alert>
                  )}

                  {/* EOB Data Section */}
                  {analysisResult.eob_data && (
                    <Card>
                      <CardHeader>
                        <Heading size="sm">Extracted EOB Data</Heading>
                      </CardHeader>
                      <CardBody>
                        <HStack spacing={8} mb={4}>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Payer Name</Text>
                            <Text fontWeight="bold">{analysisResult.eob_data.payer_name || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Total Billed</Text>
                            <Text fontWeight="bold">${analysisResult.eob_data.total_billed}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="sm" color="gray.500">Total Paid</Text>
                            <Text fontWeight="bold">${analysisResult.eob_data.total_paid}</Text>
                          </Box>
                        </HStack>
                        
                        <Table size="sm" variant="simple">
                          <Thead>
                            <Tr>
                              <Th>CPT</Th>
                              <Th>Modifiers</Th>
                              <Th>CARC</Th>
                              <Th>RARC</Th>
                              <Th isNumeric>Billed</Th>
                              <Th isNumeric>Paid</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {analysisResult.eob_data.line_items?.map((item, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium">{item.cpt_code}</Td>
                                <Td>{item.modifiers?.join(', ') || '-'}</Td>
                                <Td><Badge colorScheme="red">{item.carc}</Badge></Td>
                                <Td>{item.rarc ? <Badge colorScheme="orange">{item.rarc}</Badge> : '-'}</Td>
                                <Td isNumeric>${item.billed_amount}</Td>
                                <Td isNumeric>${item.paid_amount}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  )}

                </VStack>
              </TabPanel>
              
              <TabPanel px={0} pt={6}>
                <Card bg="white" borderColor="gray.200" h="800px">
                  <CardHeader p={4} bg="gray.50" borderBottomWidth="1px">
                    <Heading size="sm" mb={4}>Source Document Viewer</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2} borderBottomWidth="1px" pb={1}>
                          Clinical Documentation
                        </Text>
                        <VStack align="start" spacing={1}>
                          {caseFiles.filter(f => f.document_category === 'ClinicalDocumentation').length > 0 ? (
                            caseFiles.filter(f => f.document_category === 'ClinicalDocumentation').map(file => (
                              <Button 
                                key={file.id} 
                                variant="link" 
                                colorScheme={selectedFileId === file.id ? "purple" : "blue"}
                                fontWeight={selectedFileId === file.id ? "bold" : "normal"}
                                size="sm"
                                onClick={() => handleFileSelect(file.id)}
                                textAlign="left"
                                whiteSpace="normal"
                                height="auto"
                                py={1}
                              >
                                {file.file_name}
                              </Button>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No clinical documents found.</Text>
                          )}
                        </VStack>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2} borderBottomWidth="1px" pb={1}>
                          Payer Response
                        </Text>
                        <VStack align="start" spacing={1}>
                          {caseFiles.filter(f => f.document_category === 'PayerResponse').length > 0 ? (
                            caseFiles.filter(f => f.document_category === 'PayerResponse').map(file => (
                              <Button 
                                key={file.id} 
                                variant="link" 
                                colorScheme={selectedFileId === file.id ? "purple" : "blue"}
                                fontWeight={selectedFileId === file.id ? "bold" : "normal"}
                                size="sm"
                                onClick={() => handleFileSelect(file.id)}
                                textAlign="left"
                                whiteSpace="normal"
                                height="auto"
                                py={1}
                              >
                                {file.file_name}
                              </Button>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No payer response documents found.</Text>
                          )}
                        </VStack>
                      </Box>
                    </SimpleGrid>
                  </CardHeader>
                  <CardBody p={0} position="relative" bg="gray.100">
                    {isDocLoading ? (
                      <Flex justify="center" align="center" h="100%">
                        <VStack>
                          <Spinner size="xl" color="blue.500" />
                          <Text>Loading Document...</Text>
                        </VStack>
                      </Flex>
                    ) : docUrl ? (
                      <iframe 
                        src={docUrl} 
                        title="Document Viewer"
                        width="100%" 
                        height="100%" 
                        style={{ border: 'none' }} 
                      />
                    ) : (
                      <Flex justify="center" align="center" h="100%" color="gray.500" flexDirection="column" p={6} textAlign="center">
                        <Text fontSize="lg" mb={2}>No Document Selected</Text>
                        <Text fontSize="sm">Click a document link above to view it.</Text>
                      </Flex>
                    )}
                  </CardBody>
                </Card>
              </TabPanel>

              <TabPanel px={0} pt={6}>
                <Card shadow="md">
                  <CardHeader bg="gray.50" pb={2}>
                    <Heading size="md">Appeals & Denials Assistant</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Ask questions about the denial, the clinical evidence, or the coding variance to help formulate your appeal.
                    </Text>
                  </CardHeader>
                  <CardBody p={0}>
                    <ModernChat 
                      caseId={caseId} 
                      contextType="payer_response" 
                      height="600px"
                      collapsedHeight="250px"
                    />
                  </CardBody>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}

        </VStack>
      </Container>
    </Box>
  );
};

export default PayerResponseIntelligence;
